import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styles from './BrandColorsSelection.module.scss';
import ScrollContainer from '../ScrollContainer/ScrollContainer';
import cx from 'classnames';
import { areHexColorsEqual, checkIsFullyTransparent } from 'engage/utils/colorHelper';
import useOutsideColorPickerClick from 'engage/hooks/useOutsideColorPickerClick';
import { useTranslation } from 'react-i18next';
import { CustomButton } from '../Button/Button';
import { HexAlphaColorPicker, HexColorInput } from 'react-colorful';
import { ESCAPE_KEY_CODE } from 'engage/constants/common';

export const BrandingColorsSelection = ({ active = '', colors = [], additionalColors = [], allowCustom = false, onSelect, className }) => {
    const [showColorPicker, setShowColorPicker] = useState(false);
    const [pickedColor, setPickedColor] = useState(active);
    const [colorPickerStyles, setColorPickerStyles] = useState({});
    const colorPickerRef = useRef(null);

    const allColors = useMemo(() => {
        const filteredAdditionalColors = additionalColors.filter(item => !colors.find(({ color }) => areHexColorsEqual(color, item))).map(item => ({ id: `additional-color-${item}`, color: item }));
        return [...filteredAdditionalColors, ...colors];
    }, [colors, additionalColors]);

    const isCustomColor = useMemo(() => !allColors.find(item => item.color === active), [allColors, active]);

    const { t } = useTranslation();

    useOutsideColorPickerClick(colorPickerRef, () => {
        if (showColorPicker) {
            setShowColorPicker(false);
        }
    });


    const calculateColorPickerStyles = () => {
        if (colorPickerRef.current) {

            const pickerRect = colorPickerRef.current.getBoundingClientRect();
            let left = pickerRect.left;
            let top = pickerRect.top;
            const leftOffset = left + pickerRect.width - window.innerWidth;
            if (leftOffset > 0) {
                left = left - leftOffset - 20;
            }
            const topOffset = top + pickerRect.height - window.innerHeight;
            if (topOffset > 0) {
                top = top - topOffset - 20;
            }
            setColorPickerStyles({ left, top });
        }
    }
    const escapeColorPicker = (e) => {
        if (e.keyCode == ESCAPE_KEY_CODE) {
            setShowColorPicker(false);
        }
    };

    useEffect(() => {
        if (showColorPicker) {
            calculateColorPickerStyles();
            window.addEventListener('keydown', escapeColorPicker);
        } else {
            window.removeEventListener('keydown', escapeColorPicker);
        }

        return () => {
            window.removeEventListener('keydown', escapeColorPicker);
        }
    }, [colorPickerRef?.current, showColorPicker])

    const handleColorFromPicker = useCallback(() => {
        onSelect(pickedColor)
        setShowColorPicker(false);
    }, [pickedColor]);


    useEffect(() => {
        if (showColorPicker) {
            calculateColorPickerStyles();
            window.addEventListener('keydown', escapeColorPicker);
        } else {
            window.removeEventListener('keydown', escapeColorPicker);
        }

        return () => {
            window.removeEventListener('keydown', escapeColorPicker);
        }
    }, [colorPickerRef?.current,showColorPicker])

    return <ScrollContainer className={cx(styles.colorsContainer, className)} vertical={false}>
        {allColors.map(({ id, color }) =>
            <div key={id} onClick={() => onSelect(color)} role='button' tabIndex={0} className={cx(styles.colorItem, { [styles.transparent]: checkIsFullyTransparent(color), [styles.active]: active === color })} style={{ backgroundColor: color }} />
        )}

        {allowCustom && <div onClick={() => setShowColorPicker(true)} role='button' tabIndex={0} className={cx(styles.colorItem, { [styles.active]: isCustomColor }, styles.gradient)} />}

        {showColorPicker && <div ref={colorPickerRef} className={cx(styles.hexColorPicker)} style={colorPickerStyles}>
            <h3 className={styles.title}>{t('company_settings.colors.new')}</h3>
            <HexAlphaColorPicker color={pickedColor || ''} onChange={(value) => setPickedColor(value)} />
            <HexColorInput alpha={true} prefixed={true} color={pickedColor || ''} onChange={(value) => setPickedColor(value)} />
            <div className={styles.btnContainer}>
                <CustomButton label={t('general.cancel')} variant="secondary" onClick={() => setShowColorPicker(false)} className={styles.cancelButton} />
                <CustomButton label={t('general.choose')} variant="primary" onClick={handleColorFromPicker} className={styles.cancelButton} />
            </div>
        </div>}
    </ScrollContainer>
}